// Client-side store for user customizations made to Intelligence Agents in the
// Bento demo. Keeps the Agents page (deploy / pause, task & KPI tuning, integration
// connections) in sync with the Insights page, which hides insights for any agent
// that has been paused. Demo only — state lives in memory, no backend.

import {useSyncExternalStore} from "react";
import {insightAgents} from "./insightAgents";

export interface AgentSettings {
    enabled: boolean;
    mission: string;
    taskState: Record<string, boolean>;
    budgetState: Record<string, number>;
    kpiState: Record<string, {min: number; max: number}>;
    dataSourceState: Record<string, boolean>;
    integrationState: Record<string, boolean>;
}

export type AgentSettingsMap = Record<string, AgentSettings>;

const buildInitialState = (): AgentSettingsMap => {
    const map: AgentSettingsMap = {};
    for (const agent of insightAgents) {
        map[agent.id] = {
            enabled: agent.enabled,
            mission: agent.mission,
            taskState: agent.tasks.reduce<Record<string, boolean>>((acc, task) => {
                acc[task.id] = task.defaultOn;
                return acc;
            }, {}),
            budgetState: agent.budgetTargets.reduce<Record<string, number>>(
                (acc, budget) => {
                    acc[budget.id] = budget.value;
                    return acc;
                },
                {}
            ),
            kpiState: agent.kpiThresholds.reduce<
                Record<string, {min: number; max: number}>
            >((acc, kpi) => {
                acc[kpi.id] = {min: kpi.min, max: kpi.max};
                return acc;
            }, {}),
            dataSourceState: agent.dataSources.reduce<Record<string, boolean>>(
                (acc, source) => {
                    acc[source.id] = source.defaultOn;
                    return acc;
                },
                {}
            ),
            integrationState: agent.integrations.reduce<Record<string, boolean>>(
                (acc, integration) => {
                    acc[integration.id] = integration.connected;
                    return acc;
                },
                {}
            )
        };
    }
    return map;
};

let state: AgentSettingsMap = buildInitialState();
const listeners = new Set<() => void>();

const emit = (): void => {
    for (const listener of listeners) {
        listener();
    }
};

const updateAgent = (
    agentId: string,
    updater: (settings: AgentSettings) => AgentSettings
): void => {
    const current = state[agentId];
    if (!current) {
        return;
    }
    state = {...state, [agentId]: updater(current)};
    emit();
};

export const agentSettingsStore = {
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
    getSnapshot(): AgentSettingsMap {
        return state;
    },
    setEnabled(agentId: string, enabled: boolean): void {
        updateAgent(agentId, (settings) => ({...settings, enabled}));
    },
    setMission(agentId: string, mission: string): void {
        updateAgent(agentId, (settings) => ({...settings, mission}));
    },
    toggleTask(agentId: string, taskId: string): void {
        updateAgent(agentId, (settings) => ({
            ...settings,
            taskState: {
                ...settings.taskState,
                [taskId]: !settings.taskState[taskId]
            }
        }));
    },
    setBudget(agentId: string, budgetId: string, value: number): void {
        updateAgent(agentId, (settings) => ({
            ...settings,
            budgetState: {...settings.budgetState, [budgetId]: value}
        }));
    },
    setKpi(agentId: string, kpiId: string, min: number, max: number): void {
        updateAgent(agentId, (settings) => ({
            ...settings,
            kpiState: {...settings.kpiState, [kpiId]: {min, max}}
        }));
    },
    toggleDataSource(agentId: string, sourceId: string): void {
        updateAgent(agentId, (settings) => ({
            ...settings,
            dataSourceState: {
                ...settings.dataSourceState,
                [sourceId]: !settings.dataSourceState[sourceId]
            }
        }));
    },
    toggleIntegration(agentId: string, integrationId: string): void {
        updateAgent(agentId, (settings) => ({
            ...settings,
            integrationState: {
                ...settings.integrationState,
                [integrationId]: !settings.integrationState[integrationId]
            }
        }));
    }
};

export function useAgentSettings(): AgentSettingsMap {
    return useSyncExternalStore(
        agentSettingsStore.subscribe,
        agentSettingsStore.getSnapshot
    );
}
